/**
 * Shared eligibility-verdict presentation maps for the Loan Participations
 * surfaces (Marketplace browse grid, CompareDeals matrix, OfferingDetail
 * banner, Indications accept gate). One copy: the verdict vocabulary is a
 * spec enum, so its presentation mapping should not fork per page.
 */

import type { components as LspComponents } from '@aaimplatform/institutional-types/loan-syndication-protocol';

export type EligibilityVerdict = LspComponents['schemas']['EligibilityVerdict'];
export type EligibilityReason = LspComponents['schemas']['EligibilityReason'];
export type EligibilitySeverity = LspComponents['schemas']['EligibilitySeverity'];

export const ELIGIBILITY_BADGE_VARIANT: Record<EligibilityVerdict, 'success' | 'warning' | 'error' | 'neutral'> = {
  ELIGIBLE: 'success',
  WARN: 'warning',
  INELIGIBLE: 'error',
  UNKNOWN: 'neutral',
};

export const ELIGIBILITY_LABEL: Record<EligibilityVerdict, string> = {
  ELIGIBLE: 'Eligible',
  WARN: 'Review',
  INELIGIBLE: 'Ineligible',
  UNKNOWN: 'Not configured',
};

/**
 * Compact terminal-density treatment (browse grid): a bg-current dot +
 * short label colored by this tone class, replacing the wider Badge chip.
 */
export const ELIGIBILITY_TONE: Record<EligibilityVerdict, string> = {
  ELIGIBLE: 'text-growth',
  WARN: 'text-warning',
  INELIGIBLE: 'text-accent',
  UNKNOWN: 'text-tertiary',
};

export const ELIGIBILITY_SHORT_LABEL: Record<EligibilityVerdict, string> = {
  ELIGIBLE: 'Eligible',
  WARN: 'Review',
  INELIGIBLE: 'Blocked',
  UNKNOWN: 'No profile',
};

/**
 * A reason's severity in words (EligibilitySeverity in
 * loan-syndication-protocol.openapi.yaml): HARD blocks a commit, SOFT is a
 * concentration-headroom warning, INFO never changes the verdict. The Brief
 * drawer and the Indications accept gate printed the codes raw (r3 fi-g9-7).
 */
export const ELIGIBILITY_SEVERITY_LABEL: Record<EligibilitySeverity, string> = {
  HARD: 'Blocking',
  SOFT: 'Review',
  INFO: 'Note',
};
